import { inject, Injectable } from "@angular/core";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { UserService } from "@main-services/user.service";
import { PromptsFacade } from "@modules/messaging-prompts/domain/+state/prompts.facade";
import { PromptsModel } from "@modules/messaging-prompts/domain/entities/prompts.model";
import { combineLatest, filter, map, switchMap } from "rxjs";
import { AntiPhishingBlockType } from "../interfaces";
import { Helper } from "@helpers/functions";
import { getDateTimeDifference } from "@helpers/date";

@Injectable({
	providedIn: "root"
})
export class AntiPhishingCodeService {
	private readonly userService = inject(UserService);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	private readonly promptsFacade = inject(PromptsFacade);

	// private USER_APC_SETUP_DEADLINE_MINUTES = 7 * 24 * 60;
	private USER_APC_SETUP_DEADLINE_MINUTES = 4;
	// private USER_APC_FORCE_SETUP_DEADLINE_MINUTES = 24 * 60;
	private USER_APC_FORCE_SETUP_DEADLINE_MINUTES = 2;

	// We need to wait for the login to properly finish first, otherwise the promptsData() call
	// is made with no authentication, which redirects to login and results in an infinite loop

	public getAntiPhishingBlockType$ = this.userService
		.userLoggedIn()
		.pipe(
			filter((userLoggedIn) => userLoggedIn),
			switchMap(() =>
				combineLatest({
					antiPhishingFlag: this.launchDarklyService.isActive$(LD_FLAG.ANTI_PHISHING_CODE),
					antiPhishingBlockFlag: this.launchDarklyService.isActive$(LD_FLAG.ANTI_PHISHING_BLOCK_UX_IF_UNSET),
					prompts: this.promptsFacade.getPromptsData()
				})
			)
		)
		.pipe(
			map(({ antiPhishingFlag, antiPhishingBlockFlag, prompts }) => {
				if (antiPhishingFlag && antiPhishingBlockFlag && prompts) {
					const apcBlockType = this.getApcBlockType(prompts);
					return apcBlockType;
				}
				return AntiPhishingBlockType.NONE;
			})
		);

	private hasUserExceededApcSetupDeadline(apcDeadline: string): boolean {
		const diff = getDateTimeDifference(apcDeadline);
		let time = this.USER_APC_SETUP_DEADLINE_MINUTES;
		return diff >= time;
	}

	private hasUserExceededApcSkipDeadline(userSkippedSetupDate: string): boolean {
		if (userSkippedSetupDate === "") {
			return false;
		}
		const diff = getDateTimeDifference(userSkippedSetupDate);
		let time = this.USER_APC_FORCE_SETUP_DEADLINE_MINUTES;
		return diff >= time;
	}

	private getApcDeadlineFromPrompts(promptsData: PromptsModel[]): string {
		if (promptsData.length === 0) {
			return null;
		}
		const apcPrompt = promptsData.find((prompt) => prompt.event.name === "apc-prompt");
		if (apcPrompt) {
			return apcPrompt.event.created_at;
		}
		return null;
	}

	private getApcSetupSkippedDate(): string {
		const userSkippedSetupDate = Helper.getCookie("anti_phishing_code_skiped");
		if (userSkippedSetupDate === "null") {
			return "";
		}
		return userSkippedSetupDate;
	}

	private getApcBlockType(promptsData: PromptsModel[]): AntiPhishingBlockType {
		let apcBlockType = AntiPhishingBlockType.NONE;
		if (promptsData.length > 0 && this.hasApcPrompt(promptsData)) {
			const userApcSetupDeadline = this.getApcDeadlineFromPrompts(promptsData);
			const shouldDisplayApcModal = this.hasUserExceededApcSetupDeadline(userApcSetupDeadline);
			const userSkippedSetupDate = this.getApcSetupSkippedDate();
			const hasUserExceededApcSkipDeadline = this.hasUserExceededApcSkipDeadline(userSkippedSetupDate);

			if (shouldDisplayApcModal && userSkippedSetupDate === "") {
				apcBlockType = AntiPhishingBlockType.HALF;
			}
			if (hasUserExceededApcSkipDeadline) {
				apcBlockType = AntiPhishingBlockType.FULL;
			}
		}
		return apcBlockType;
	}

	private hasApcPrompt(promptsData: PromptsModel[]): boolean {
		return promptsData.some((prompt) => prompt.event?.name === "apc-prompt");
	}
}
